import { createSelector } from 'reselect';

import { getAreActivitiesEnabled } from 'lib/selectors/getAreActivitiesEnabled';
import { getLatestActivitySet } from 'lib/selectors/getLatestActivitySet';
import { getLatestSetupActivityStartAt } from 'lib/selectors/getLatestSetupActivityStartAt';
import { getLatestSetupActivityEndAt } from 'lib/selectors/getLatestSetupActivityEndAt';
import { getClosedSetupAggMs } from 'lib/selectors/getClosedSetupAggMs';
import { getJobRun } from 'lib/selectors/getJobRun';
import { getJobRunASWrapper } from 'lib/selectors/getJobRunASWrapper';
import { getPostLaborUpdatesOnNats } from 'lib/selectors/getPostLaborUpdatesOnNats';
import { getCurrentLaborTicket } from 'lib/selectors/getLaborTickets';
import { getIsPaused } from 'lib/selectors/getIsPaused';
import {
  getLatestSetupTicketClockOut,
  getSetupLaborTicketsForOperation,
} from 'lib/selectors/getSetupLaborTicketsForOperation';
import { getUnitDurationFromString, dateUTC } from 'lib/utils/date';

const getNatsSetupLaborDurationAnchor = (
  currentLaborTicket,
  setupTickets,
  isPaused
) => {
  const isSetupTicket = currentLaborTicket?.type?.toLowerCase() === 'setup';
  const isActiveOpenSetup =
    isSetupTicket && currentLaborTicket?.state === 'OPEN' && !isPaused;

  if (isActiveOpenSetup && currentLaborTicket?.clockIn) {
    return dateUTC(currentLaborTicket.clockIn);
  }

  if (isPaused && isSetupTicket && currentLaborTicket?.clockOut) {
    return dateUTC(currentLaborTicket.clockOut);
  }

  const latestClockOut = getLatestSetupTicketClockOut(setupTickets);
  return latestClockOut ? dateUTC(latestClockOut) : null;
};

/**
 * uses agg of elapsed setup time to produce setup dates w/ negative start offset for duration ticker
 */
const getAdjustedSetupDurationDates = createSelector(
  [
    getAreActivitiesEnabled,
    getLatestActivitySet,
    getLatestSetupActivityStartAt,
    getLatestSetupActivityEndAt,
    getClosedSetupAggMs,
    getJobRun,
    getJobRunASWrapper,
    getPostLaborUpdatesOnNats,
    getCurrentLaborTicket,
    getSetupLaborTicketsForOperation,
    getIsPaused,
  ],
  (
    areActivitiesEnabled,
    latestActivitySet,
    latestSetupActivityStartAt,
    latestSetupActivityEndAt,
    closedSetupAggMs,
    jobRun,
    jobRunASWrapper,
    natsFlowEnabled,
    currentLaborTicket,
    setupTickets,
    isPaused
  ) => {
    // NATS mode: mirror activities — anchor minus closed setup labor ticket time
    if (natsFlowEnabled && areActivitiesEnabled) {
      const setupLaborDurationAnchor = getNatsSetupLaborDurationAnchor(
        currentLaborTicket,
        setupTickets,
        isPaused
      );

      if (setupLaborDurationAnchor) {
        const adjustedSetupStartDate = dateUTC(
          setupLaborDurationAnchor
        ).subtract(closedSetupAggMs, 'milliseconds');
        const { expectedSetupDuration } = latestActivitySet;
        const expectedSetupDurationMs = getUnitDurationFromString(
          expectedSetupDuration,
          'milliseconds'
        );
        return {
          adjustedSetupStartDate: dateUTC(adjustedSetupStartDate),
          expectedSetupEndDate: dateUTC(adjustedSetupStartDate).add(
            expectedSetupDurationMs,
            'milliseconds'
          ),
        };
      }

      return {
        adjustedSetupStartDate: null,
        expectedSetupEndDate: null,
      };
    }

    if (areActivitiesEnabled) {
      if (latestSetupActivityStartAt) {
        // if open, offset start of setup activity-in-progress (handle "live" end in components);
        // otherwise, offset end of last setup activity by closed setup time;
        const startAt = dateUTC(
          latestSetupActivityEndAt || latestSetupActivityStartAt
        );
        const adjustedSetupStartDate = dateUTC(startAt).subtract(
          closedSetupAggMs,
          'milliseconds'
        );
        const { expectedSetupDuration } = latestActivitySet;
        const expectedSetupDurationMs = getUnitDurationFromString(
          expectedSetupDuration,
          'milliseconds'
        );
        return {
          adjustedSetupStartDate: dateUTC(adjustedSetupStartDate),
          expectedSetupEndDate: dateUTC(adjustedSetupStartDate).add(
            expectedSetupDurationMs,
            'milliseconds'
          ),
        };
      }
    } else if (jobRun && jobRun.startDate) {
      const { expectedSetupDuration } = jobRunASWrapper;
      const expectedSetupDurationMs = getUnitDurationFromString(
        expectedSetupDuration,
        'milliseconds'
      );
      return {
        adjustedSetupStartDate: dateUTC(jobRun.startDate),
        expectedSetupEndDate: dateUTC(jobRun.startDate).add(
          expectedSetupDurationMs,
          'milliseconds'
        ),
      };
    }

    return {
      adjustedSetupStartDate: dateUTC(),
      expectedSetupEndDate: dateUTC(),
    };
  }
);

export { getAdjustedSetupDurationDates };
